import Campaign from "@/types/Campaign.interface"
import { CampaignParams } from "@/types/PaginationParams.interface"
import Group from "@/types/Group.interface"
import Pagination from "@/types/Pagination.interface"

interface State {
    autorespondersCampaigns: Campaign[]
    autorespondersCampaignsCurrentPage: number
    autorespondersCampaignsLastPage: number
    autorespondersCampaignsNextPage: number
    autorespondersCampaignsPaginating: boolean
    autorespondersCampaignsParams: CampaignParams
    autorespondersCampaignsParamsDefault: CampaignParams
    autorespondersSelectedCampaign: Campaign
    autorespondersSelectedGroup: Group
}

const defaults = () => {
    return {
        autorespondersCampaigns: [],
        autorespondersCampaignsCurrentPage: null,
        autorespondersCampaignsLastPage: null,
        autorespondersCampaignsNextPage: null,
        autorespondersCampaignsPaginating: false,
        autorespondersCampaignsParams: { page: 1, type: 'all' },
        autorespondersCampaignsParamsDefault: { page: 1, type: 'all' },
        autorespondersSelectedCampaign: null,
        autorespondersSelectedGroup: null
    }
}

export default {
    state: defaults(),
    mutations: {
        autorespondersCampaignsParamsReset (state: State): void {
            state.autorespondersCampaignsParams = state.autorespondersCampaignsParamsDefault
        },
        autorespondersCampaignsParamsSet (state: State, params: CampaignParams): void {
            state.autorespondersCampaignsParams = params
        },
        autorespondersCampaignsPush (state: State, campaigns: Campaign[]): void {
            state.autorespondersCampaigns = window.removeDuplicatesById(state.autorespondersCampaigns, campaigns)

            //  Get the currently selected campaign
            const selectedCampaign = state.autorespondersSelectedCampaign

            //  If the campaign does NOT exist, do nothing
            if (!selectedCampaign) return

            //  Else, find if they are in the updated campaigns list
            const updatedCampaign = campaigns.find((x: Campaign) => x.id == selectedCampaign.id)

            //  If not found, then do nothing
            if (!updatedCampaign) return

            //  Else, updated the selected campaign
            state.autorespondersSelectedCampaign = updatedCampaign
        },
        autorespondersCampaignsRemove (state: State, ids: string[]): void {
            //  Get our current lists of campaigns
            const campaigns = state.autorespondersCampaigns

            //  If no campaigns, do nothing
            if (!campaigns) return

            //  Else, filter out our removed ids
            const filtered = campaigns.filter((x: Campaign) => !ids.includes(x.id))

            //  Replace with the filtered list
            state.autorespondersCampaigns = filtered

            //  Get the currently selected campaign
            const selectedCampaign = state.autorespondersSelectedCampaign

            //  If the campaign does NOT exist, then do nothing
            if (!selectedCampaign) return

            //  Else, check if they are in our array
            const deleted = ids.find((x: string) => x == selectedCampaign.id)

            //  If not found, do nothing
            if (!deleted) return

            //  Else, set the selected campaign to null since it is now deleted
            state.autorespondersSelectedCampaign = null
        },
        autorespondersCampaignsSync (state: State, updates: Campaign[]): void {
            //  Get our list of campaigns
            const campaigns = state.autorespondersCampaigns

            //  If no campaigns, do nothing
            if (!campaigns) return

            //  Get our autoresponders params
            const params = state.autorespondersCampaignsParams

            //  Apply our filtering
            const filtered = updates.appSyncFilter(params, 'autoresponders')

            //  Find the items we will have to remove because of the filter
            const remove = window._.differenceBy(updates, filtered, 'id')

            //  Update our list with the filtered array
            state.autorespondersCampaigns = window.removeDuplicatesById(state.autorespondersCampaigns, filtered, remove)

            //  Check if we have a selected campaign
            const selected = state.autorespondersSelectedCampaign

            //  If it doesn't exist, do nothing
            if (!selected) return

            //  Else, check if our filtered contains this ids
            const exists = updates.find((x: Campaign) => x.id == selected.id)

            //  If it does NOT contain the id, do nothing
            if (!exists) return

            //  Else, update our selected campaign
            state.autorespondersSelectedCampaign = exists
        },
        autorespondersPaginationPush (state: State, page: Pagination): void {
            //  If this is page one, reset our campaigns list
            if (page.current_page === 1) state.autorespondersCampaigns = []

            state.autorespondersCampaignsCurrentPage = page.current_page
            state.autorespondersCampaignsLastPage = page.last_page
            state.autorespondersCampaignsNextPage = page.current_page + 1
        },
        autorespondersCampaignsPaginatingSet (state: State, bool: boolean): void {
            state.autorespondersCampaignsPaginating = bool
        },
        autorespondersSelectedCampaignSet (state: State, campaign: Campaign): void {
            state.autorespondersSelectedCampaign = campaign
        },
        autorespondersSelectedGroupRemove (state: State, ids: number[]): void {
            //  Check if we have a selected group
            const selectedGroup = state.autorespondersSelectedGroup

            //  If it doesn't exist, we are done
            if (!selectedGroup) return

            //  Else, check if our removed ids contains this id
            const deleted = ids.find((x: number) => x === selectedGroup.id)

            //  If it has not been deleted, do nothing
            if (!deleted) return

            //  Else, remove our selected group
            state.autorespondersSelectedGroup = null
        },
        autorespondersSelectedGroupSet (state: State, group: Group): void {
            state.autorespondersSelectedGroup = group
        },
        autorespondersSelectedGroupSync (state: State, updates: Group[]): void {
            //  Check if we have a selected group
            const selected = state.autorespondersSelectedGroup

            //  If it doesn't exist, do nothing
            if (!selected) return

            //  Else, check if our updates contains this id
            const exists = updates.find((x: Group) => x.id == selected.id)

            //  If it does NOT contain the id, do nothing
            if (!exists) return

            //  Else, update our selected group
            state.autorespondersSelectedGroup = exists
        },
        autorespondersReset (state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        autorespondersCampaignsPaginate ({ commit, getters }): Promise<Pagination> {
            //  Select our pagination params
            const p = getters.autorespondersCampaignsParams

            //  Start the paginating tracker
            commit('autorespondersCampaignsPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/campaigns?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our pagination params
                commit('autorespondersPaginationPush', response.data)

                //  Update teh autoresponders campaigns list
                commit('autorespondersCampaignsPush', response.data.data)

                //  Stop the paginating tracker
                commit('autorespondersCampaignsPaginatingSet', false)

                //  Return the pagination data
                return response.data
            }).catch(e => console.error("AuthRespondersCampaignsPaginate ERROR", e))
        },
        autorespondersSelectedCampaignGet ({ commit, getters }, campaignId: string): Promise<Campaign> {
            //  Grab the global campaigns list
            const campaigns = getters.campaigns

            //  Check if this campaign already exists within
            const existingCampaign = campaigns.find((x: Campaign) => x.id == campaignId)

            //  If there IS an existing campaign, commit that and proceed
            if (existingCampaign) return commit('autorespondersSelectedCampaignSet', existingCampaign)

            //  Else, assemble the API endpoint
            const url = `${getters.api}/campaigns/${campaignId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the selected campaign
                commit('autorespondersSelectedCampaignSet', response.data)

                //  Push into our global dataset
                commit('campaignsPush', [response.data])

                //  Return our campaign
                return response.data
            }).catch(() => null)
        },
        autorespondersSelectedGroupGet ({ commit, getters }, campaign: Campaign): Promise<Group> {
            //  Grab the group id from the campaign
            const groupId = campaign.group_id

            //  Get the global groups list
            const groups = getters.groups

            //  Search for the existing group in the global dataset
            const existingGroup = groups.find((x: Group) => x.id == groupId)

            //  If there is an existing group then commit and return it
            if (existingGroup) return commit('autorespondersSelectedGroupSet', existingGroup)

            //  Else, assemble our API endpoint
            const url = `${getters.api}/groups/${groupId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the autoresponders selected group
                commit('autorespondersSelectedGroupSet', response.data)

                //  Add this group to our global dataset
                commit('groupsPush', [response.data])

                //  Return the found group
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        autorespondersCampaigns (state: State): Campaign[] {
            return state.autorespondersCampaigns
        },
        autorespondersCampaignsCurrentPage (state: State): number {
            return state.autorespondersCampaignsCurrentPage
        },
        autorespondersCampaignsLastPage (state: State): number {
            return state.autorespondersCampaignsLastPage
        },
        autorespondersCampaignsNextPage (state: State): number {
            return state.autorespondersCampaignsNextPage
        },
        autorespondersCampaignsPaginating (state: State): boolean {
            return state.autorespondersCampaignsPaginating
        },
        autorespondersCampaignsParams (state: State): CampaignParams {
            return state.autorespondersCampaignsParams
        },
        autorespondersCampaignsParamsDefault (state: State): CampaignParams {
            return state.autorespondersCampaignsParamsDefault
        },
        autorespondersSelectedCampaign (state: State): Campaign {
            return state.autorespondersSelectedCampaign
        },
        autorespondersSelectedGroup (state: State): Group {
            return state.autorespondersSelectedGroup
        }
    }
}
